import { IComponentService } from './ComponentService.types';
import { ISelectOptions, ISelectOption } from 'react-ocean-forms-bootstrap';
import { ComponentTypeApi } from './ComponentTypeApi';
import { StatusApi } from './StatusApi';
import { KomponenteServiceApi } from './services';
import { ComponentFormValues, BaseComponentFormValues } from '../components/ParamComponents/ComponentForm.types';
import { KomponenteDto, KomponenteCreateDto, ComponentDuplicates } from './dto';
import { StatusList, Status, ComponentTypeList } from './model';
import { ComponentReleasesDto } from './dto/ComponentReleasesDto';
import { RestResponse } from '@oceanjs/core';
import { KomponentenStatusUtil } from './KomponentenStatusUtil';
import { ServiceError } from './errors';
import { handleServiceError, ServiceUtil } from './ServiceUtil';
import { OptionalKomponentenTypEnum, KomponentenTypEnum } from '../components/ParamComponents';
import { KomponentenTypUtil } from './KomponentenTypUtil';
import moment from 'moment';
import { DimensionQuery } from '../graphql/Components';
import { Alternativname } from '../generated/types';


class ComponentServiceImpl implements IComponentService {

  private statusList: StatusList = null;

  private typeList: ComponentTypeList = [];

  async createComponent(values: ComponentFormValues): Promise<any> {

    const dto = this.transformFormToDTO(values);
    console.log('create', values, dto);
    const result: RestResponse<KomponenteDto> = await KomponenteServiceApi.createKomponente(dto, handleServiceError);
    if(result?.status === 201) {
      return result.data;
    }
    throw new ServiceError(500, {message: 'Unbekannter Fehler', status: 500});
  }
  
  async editComponent(id: number, values: ComponentFormValues): Promise<any> {
    const dto = this.transformFormToDTO(values);
    const result: RestResponse<KomponenteDto> = await KomponenteServiceApi.editKomponente(id, dto, handleServiceError);
    if (result?.status === 200) {
      return result.data;
    }
    throw new ServiceError(500, {message: 'Unbekannter Fehler', status: 500});
  }

  async releaseComponent(releases: ComponentReleasesDto): Promise<ComponentReleasesDto> {
  
    const result = await KomponenteServiceApi.releaseKomponente(releases, handleServiceError);
    console.log('component released ', releases, result);
    if(result) {
      return result?.data;
    }
    throw new ServiceError(500, {message: 'Unbekannter Fehler', status: 500});
  }

  async deleteComponent(type: string, id: number, anmerkung: string): Promise<void> {
    const dto = { anmerkung };
    
    const result: RestResponse<any> = await KomponenteServiceApi.deleteKomponente(type, id, dto, handleServiceError);
    console.log('delete-result', result);
    if(result?.status === 204) {
      return ;
    }
    throw new ServiceError(500, {message: 'Unbekannter Fehler', status: 500});
  }

  /**
   * check duplicates and get list of ids and matchees, 
   * @param values 
   */
  async checkDuplicates(values: ComponentFormValues, id: number | undefined ): Promise<ComponentDuplicates> {
    const dto = this.transformFormToDTO(values);
    if(id) dto.id = id;
    console.log('check duplicates', values, dto);
    const result = await KomponenteServiceApi.checkKomponenteDuplikate(dto, handleServiceError);
    if(result) {
      return result?.data;
    }
    throw new ServiceError(500, {message: 'Unbekannter Fehler', status: 500});
  }

  /**
   * 
   * @param values form data
   */
  transformFormToDTO(values: ComponentFormValues): KomponenteCreateDto {
    const dto: KomponenteCreateDto = {
      name: values.name,
      anforderung: ServiceUtil.transformRequestInfoFormToDto(values.anforderung),
      type:  KomponentenTypUtil.getComponentTypeEnum(values.component_name.value),
      status: KomponentenStatusUtil.getStatusEnumFromStatus(values.status.value),
    }

    switch (values.kind){
      case KomponentenTypEnum.Dimension:
        dto.symbol = values.symbol;
        break
      // TODO alternative name 
      case KomponentenTypEnum.Stoff:
        dto.anmerkung = values.annotations;
        dto.casNummer = values.casnumber;
        dto.egNummer = values.eunumber;
        dto.formel = values.sumformula;
        dto.alternativnamen = this.transformAlternativenamenOptionsToString(values.alternativnamen);
        break;
      case KomponentenTypEnum.Einheit:
        dto.dimension = ServiceUtil.transformSelectJsonToKompId(values.dimension);
        dto.isocode = values.iso_code;
        dto.symbol = values.symbol;
        break;
      case KomponentenTypEnum.Methode:
        dto.gueltigAb = values.valid_from ? moment(values.valid_from).format('DD.MM.YYYY') : '';
        dto.titel = values.titel;
        dto.anmerkung = values.annotations;
        break;
      case KomponentenTypEnum.Probenvorbehandlung:
      case KomponentenTypEnum.Nichtstofflich:
      case KomponentenTypEnum.SonstigesKriterium:
      case KomponentenTypEnum.Matrix:
        dto.anmerkung = values.annotations;
        break;
    }
  
    return dto;
  }

  /**
   * 
   * @param values 
   */
  transformDtoToForm =  (type: OptionalKomponentenTypEnum, dto: KomponenteDto): ComponentFormValues | {} => {

    console.log('dto to from', dto);
    // fix rendering issues
    if (dto === null) return {};
    
    const status = KomponentenStatusUtil.getStatusOption(KomponentenStatusUtil.getStatus(dto.status.toString()));
    const compTypeOption = KomponentenTypUtil.getComponentOptionForEnum(type);
    const base: BaseComponentFormValues = {
      component_name: compTypeOption,
      code: dto.code ?? '',
      name: dto.name,
      status,
      anforderung:  { ...dto.anforderung }
    }

    if(type === undefined) {
      throw new Error('Unbekannter Komponententyp');
    }

    switch(type) {
      case KomponentenTypEnum.Dimension:
        return {
          kind: type,
          ...base,
          symbol: dto.symbol,
        }
      case KomponentenTypEnum.Stoff:
        return {
          kind: type,
          ...base,
          casnumber: dto.casNummer,
          eunumber: dto.egNummer,
          sumformula: dto.formel,
          annotations: dto.anmerkung,
          alternativnamen: this.transformAlternativenamenToOptions(dto.alternativnamen)
        }
      case KomponentenTypEnum.Nichtstofflich:
        return {
          kind: type,
          ...base,
          annotations: dto.anmerkung
        }
      case KomponentenTypEnum.Einheit:
        return {
          kind: type,
          ...base,
          // TODO EINPAR-532
          // @ts-ignore
          dimension: dto.dimension ? DimensionQuery.getSelectOption(dto.dimension) : undefined,
          iso_code: dto.isocode,
          symbol: dto.symbol,
        }
      case KomponentenTypEnum.Probenvorbehandlung:
        return {
          kind: type,
          ...base,
          annotations: dto.anmerkung,
        }
      case KomponentenTypEnum.Methode:
        return {
          kind: type,
          ...base,
          annotations: dto.anmerkung,
          titel: dto.titel,
          valid_from: dto.gueltigAb,
        } 
      case KomponentenTypEnum.SonstigesKriterium:
        return {
          kind: type,
          ...base,
          annotations: dto.anmerkung,
        }
      case KomponentenTypEnum.Matrix:
        return {
          kind: type,
          ...base,
          annotations: dto.anmerkung,
        }
      default:
        throw new Error('Unbekannter Komponententyp');
    }
  }


  /**
   * 
   * @param selected SelectOptions or string because forms return an empty string 
   */
  transformAlternativenamenOptionsToString(selected: ISelectOptions | string): string[] {
    if(typeof selected === 'string') {
      return [];
    }
    const selectedOptions = selected as ISelectOptions;
    const result = selectedOptions.map(e => e.value);
    return result;
  }

  transformAlternativenamenToOptions(selected: Alternativname[] | undefined): ISelectOptions {
    if(!selected) return [];
    // TODO Fix this
    const result = selected.map<ISelectOption>(e => {
      return { label: e.name ?? '', value: e.name ?? '' };
    });
    return result;
  }



  findAllComponentTypeOptions = async (): Promise<ISelectOptions> => {
  
    const list = await this.findAllComponentTypes();

    this.typeList = list;
    return list.map(e => {
      return {label: e.name, value: e.id }
    });
  }


  /**
   *
   */
  findAllComponentTypes = async (): Promise<ComponentTypeList> => {

    if (this.typeList.length === 0) {
      const result = await ComponentTypeApi.findAll();
      if (result && result.data) {
        this.typeList = result.data._embedded.enumValueObjects.map( e => {
          return { name: e.name, id: e.id};
        });
      }
    }

    return this.typeList;
  }

  getComponentTypes = (): ComponentTypeList => {
    return this.typeList;
  }

  getComponentTypeName = async(id: string): Promise<string|undefined> => {

    const list = await this.findAllComponentTypes();

    const result = list.find(e => {
      return e.id === id;
    });
    return result?.name;
  }

  /**
   * 
   */
  async findComponentTypeForLabel(label: string): Promise<ISelectOption | undefined> {

    const list = await this.findAllComponentTypeOptions();

    const result = list.find(e => {
      return e.label === label});

    console.log('find option', list, result);
    return result;
  }

  /**
   *
   */
  public getComponentStatusOptions = async(): Promise<ISelectOptions> => {

    const options: ISelectOptions = [];

    this.getComponentStatusList()

    const result = await this.getComponentStatusList();
    result?.forEach((e) => {
      options.push(KomponentenStatusUtil.getStatusOption(e));
    });
    
    return options;
  }

  public getComponentStatusOption = (id: string): ISelectOption | null => {
    const result = this.getComponentStatus(id);

    if (result) {
      return KomponentenStatusUtil.getStatusOption(result);
    }
    return null;
  }

  public getComponentStatus = (id: string): Status|null  => {
    if (this.statusList && this.statusList.length > 0) {
      const filtered = this.statusList?.filter(e => e.id === id);
      if(filtered) {
        return filtered[0];
      }
    }
    return null;
  }

  private getComponentStatusList = async(): Promise<StatusList> => {
    if(!this.statusList || this.statusList.length === 0) {
      const result = await StatusApi.getComponentStatus();
      console.log('NETWORK', result);
      if (result && result.data) {
        this.statusList = result.data._embedded.content.map((e)=> {
          return e;
        });
      }
    }
    return this.statusList;
  }
  


}

export const ComponentService = new ComponentServiceImpl();
